import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { AdminProfileErrorCode, CommonErrorCode } from '~src/commonInfra/utils/ErrorCodes';

import {
    AdminProfileDialogBody,
    AdminProfileDialogProps,
    AdminProfileDialogState,
} from '../components/AdminProfileDialog';
import { IAdminProfileDetails } from '../models/IAdminProfile';
import { IAdminProfileTemplate } from '../models/IAdminProfileTemplate';
import { IUpdateAdminProfileResult } from '../requests/UpdateAdminProfileRequest';
import { AdminProfilesService } from './AdminProfilesService';

const DIALOG_WIDTH = 1200 as TabDialogWidth;

function buildInitialDialogState(profile: IAdminProfileDetails): AdminProfileDialogState {
    const selectedPermissions = profile.permissions
        .filter(p => p.enabled)
        .map(p => ({ category: p.category, capability: p.name }));

    return {
        name: profile.name,
        description: profile.description,
        selectedPermissions,
    };
}

function isActionDisabled(bodyProps: AdminProfileDialogProps, state: AdminProfileDialogState): boolean {
    if (!state.name.trim() || bodyProps.isLoadingTemplate) {
        return true;
    }
    return bodyProps.existingProfileNames.some(
        existing => existing.toLowerCase() === state.name.trim().toLowerCase()
    );
}

export function showEditAdminProfileDialog(
    template: IAdminProfileTemplate | null,
    profile: IAdminProfileDetails,
    existingProfileNames: string[],
    onSuccess: (luid: string) => void
): void {
    const initialBodyProps: AdminProfileDialogProps = {
        template,
        isLoadingTemplate: false,
        existingProfileNames,
    };

    const initialDialogState = buildInitialDialogState(profile);

    openReactActionDialog<AdminProfileDialogProps, AdminProfileDialogState, IUpdateAdminProfileResult>({
        title: Messages.editAdminProfileDialogTitle(),
        bodyComponent: AdminProfileDialogBody,
        initialBodyProps,
        initialDialogState,
        actionName: (_bodyProps, dialogState) => {
            const count = dialogState.selectedPermissions.length;
            return count > 0
                ? Messages.editAdminProfileActionWithCount({ COUNT: String(count) })
                : Messages.editAdminProfileDialogAction();
        },
        actionDisabled: isActionDisabled,
        action: updateAdminProfileAction(profile.luid, onSuccess),
        dismissDialogOnActionFailed: false,
        dialogWidth: DIALOG_WIDTH,
    });
}

function updateAdminProfileAction(luid: string, onSuccess: (luid: string) => void) {
    return async (
        _bodyProps: AdminProfileDialogProps,
        dialogState: AdminProfileDialogState,
        updateDialogState: React.Dispatch<MergeOrUpdateStateAction<AdminProfileDialogState>>
    ): Promise<IUpdateAdminProfileResult> => {
        const result = await AdminProfilesService.updateAdminProfile({
            luid,
            name: dialogState.name,
            description: dialogState.description,
            permissions: dialogState.selectedPermissions.map(p => ({
                capability: p.capability,
                resource: p.category,
            })),
        });
        return notify(updateDialogState, result, onSuccess);
    };
}

function notify(
    updateDialogState: React.Dispatch<MergeOrUpdateStateAction<AdminProfileDialogState>>,
    result: IUpdateAdminProfileResult,
    onSuccess: (luid: string) => void
): Promise<IUpdateAdminProfileResult> {
    if (!!result.errors && result.errors.length > 0) {
        const errorMsg: string = errorMessageFor(Number(result.errors[0].code));
        if (errorMsg) {
            updateDialogState({ errorMessage: errorMsg });
            return Promise.reject();
        } else {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editAdminProfileUpdateFailed() });
            return Promise.resolve(result);
        }
    } else {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.editAdminProfileDialogTitle() });
        onSuccess(result.luid);
        return Promise.resolve(result);
    }
}

function errorMessageFor(errorCode: number): string {
    switch (errorCode) {
        case AdminProfileErrorCode.InvalidName:
            return Messages.createAdminProfileInvalidName();
        case CommonErrorCode.UnexpectedParameterValue:
            return Messages.editAdminProfileInvalidParams();
        default:
            return undefined;
    }
}

export const EditAdminProfileDialogAction = {
    notify,
    isActionDisabled,
    errorMessageFor,
};

export type EditAdminProfileDialogAction = typeof EditAdminProfileDialogAction;
